import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, message } from 'antd';
import { useAuthStore } from '@stores/auth.store';
import { useCanCheck } from '@/ability/helpers';
import {
    PROJECT_BLOCKERS_UPDATE,
    PROJECT_TASKS_VIEW_ABILITY,
    PROJECT_TASKS_UPDATE_ABILITY,
} from '@/ability';
import { deleteProjectTaskBlocker } from '@services/projects.service';
import {
    formatActivityLogDateTime,
    getAttachmentFileExtension,
    getAvatarColor,
    getBugDescriptionMarkdown,
    getBugHeading,
    getInitials,
    getTaskAttachmentListForModal,
    getTaskDisplayId,
    isLikelyFileAttachmentUrl,
    isLikelyImageAttachmentUrl,
    isTaskOnDevBacklog,
    normalizePhaseLinkUrl,
    normalizeSubmissionAttachmentRecords,
    resolveAttachmentUrl,
    truncateFileName,
} from '@hooks/projects/projectHelpers';
import { formatLongDateTimeWithPipe } from '@utils/dateFormating.util';
import { resolveDescriptionMentionsForDisplay } from '@utils/richTextMentions';

function toMarkdownString(value) {
    if (!value) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'object' && typeof value.markdown === 'string') return value.markdown.trim();
    return '';
}

function normalizeBugAttachmentItems(bug) {
    if (!bug) return [];
    const merged = [];
    const pushAll = (items) => {
        if (!Array.isArray(items)) return;
        for (const it of items) merged.push(it);
    };
    pushAll(bug.attachments);
    pushAll(bug.links);
    pushAll(bug.files);
    const descAtt =
        bug.description && typeof bug.description === 'object' && Array.isArray(bug.description.attachments)
            ? bug.description.attachments
            : [];
    pushAll(descAtt);

    const seen = new Set();
    const out = [];
    for (const x of merged) {
        if (!x) continue;
        if (typeof x === 'string') {
            const url = x.trim();
            if (!url) continue;
            const type = isLikelyFileAttachmentUrl(url) ? 'file' : 'link';
            const key = `${type}|${url}`;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push({ type, url, title: url });
            continue;
        }
        const url = String(x.url ?? x.href ?? '').trim();
        if (!url) continue;
        const rawType = String(x.type ?? '').toLowerCase();
        const inferred = isLikelyFileAttachmentUrl(url) ? 'file' : 'link';
        const type = rawType === 'file' || rawType === 'link' ? rawType : inferred;
        const title = String(x.title ?? x.name ?? x.filename ?? url);
        const key = `${type}|${url}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ type, url, title });
    }
    return out;
}

export function useTaskDetailModal(props) {
    const {
        open,
        projectId,
        task,
        taskDescriptionMarkdown,
        taskBugs,
        onRequestRefresh,
    } = props;

    const [detailTab, setDetailTab] = useState('attachments');
    const [discussionBug, setDiscussionBug] = useState(null);
    const [discussionBlocker, setDiscussionBlocker] = useState(null);
    const [descriptionPreviewImage, setDescriptionPreviewImage] = useState('');
    const [createBlockerOpen, setCreateBlockerOpen] = useState(false);
    const [editingBlocker, setEditingBlocker] = useState(null);
    const bugFileInputRef = useRef(null);
    const [hoveredBlockerId, setHoveredBlockerId] = useState(null);
    const [hiddenBlockerIds, setHiddenBlockerIds] = useState(() => new Set());

    useEffect(() => {
        if (open) {
            setDetailTab('attachments');
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            setDiscussionBug(null);
            setDiscussionBlocker(null);
        }
    }, [open]);

    useEffect(() => {
        setHiddenBlockerIds(new Set());
        setHoveredBlockerId(null);
    }, [open, task?.id, task?.taskId, task?.task_id]);

    const taskTypeLabel = useMemo(() => {
        const raw = task?.type ?? task?.taskType ?? task?.task_type ?? task?.category ?? task?.kind ?? '';
        const t = String(raw ?? '').trim().toLowerCase();
        const normalized = t || 'feature';
        if (normalized === 'in_house') return 'In House';
        return normalized
            .replace(/_/g, ' ')
            .split(/\s+/)
            .filter(Boolean)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(' ');
    }, [task]);

    const hasDescription = Boolean(taskDescriptionMarkdown && String(taskDescriptionMarkdown).trim().length > 0);

    const taskDescriptionMentions = useMemo(
        () => task?.descriptionMentions ?? task?.description_mentions ?? null,
        [task?.descriptionMentions, task?.description_mentions],
    );

    const resolvedTaskDescriptionMarkdown = useMemo(
        () => resolveDescriptionMentionsForDisplay(String(taskDescriptionMarkdown || '').trim(), taskDescriptionMentions),
        [taskDescriptionMarkdown, taskDescriptionMentions],
    );
    const attachmentItems = useMemo(() => (task ? getTaskAttachmentListForModal(task) : []), [task]);
    const attachmentCount = attachmentItems.length;
    const bugs = Array.isArray(taskBugs) ? taskBugs : [];
    const bugCount = bugs.length;
    const activityLogs = useMemo(() => {
        const raw = task?.activity_logs ?? task?.activityLogs ?? task?.activity ?? [];
        return Array.isArray(raw) ? raw : [];
    }, [task]);
    const activityCount = activityLogs.length;
    const showActivityTab = true;
    const taskBlockers = useMemo(() => {
        const raw = task?.taskBlockers ?? task?.task_blockers ?? task?.blockers ?? task?.task_blockers_list ?? [];
        return Array.isArray(raw) ? raw : [];
    }, [task]);
    // blockerCount is computed from the filtered presentation list below (supports optimistic delete).

    const attachmentLinks = useMemo(
        () => attachmentItems.filter((x) => x?.kind === 'link' && x?.rawUrl && !isLikelyFileAttachmentUrl(x.rawUrl)),
        [attachmentItems],
    );
    const attachmentFiles = useMemo(
        () => attachmentItems.filter((x) => x?.rawUrl && (x.kind !== 'link' || isLikelyFileAttachmentUrl(x.rawUrl))),
        [attachmentItems],
    );

    const descriptionAttachmentItems = useMemo(
        () =>
            attachmentItems.map((x) => ({
                type: x.kind === 'link' ? 'link' : 'file',
                url: x.rawUrl,
                title: x.name,
            })),
        [attachmentItems],
    );

    const resolveAttachmentHrefFromItems = useCallback((href, items) => {
        if (!href) return '';
        const m = /^#attachment-(\d+)$/i.exec(href);
        if (!m) return '';
        const idx = Number(m[1]);
        if (Number.isNaN(idx) || idx < 0) return '';
        const source = Array.isArray(items) ? items : [];
        const filesOnly = source.filter((x) => String(x?.type || '').toLowerCase() !== 'link');
        const target = filesOnly[idx] ?? source[idx];
        return target?.url ? resolveAttachmentUrl(target.url) : '';
    }, []);

    const handleMarkdownImageLinkClick = useCallback(
        ({ event, href, rawHref, isImage }, items) => {
            if (!href) return;
            if (/^#attachment-\d+$/i.test(String(rawHref || ''))) {
                const resolved = resolveAttachmentHrefFromItems(String(rawHref), items);
                if (!resolved) return;
                event?.preventDefault?.();
                event?.stopPropagation?.();
                if (isLikelyImageAttachmentUrl(resolved)) {
                    setDescriptionPreviewImage(resolved);
                    return;
                }
                window.open(resolved, '_blank', 'noopener,noreferrer');
                return;
            }
            if (isImage || isLikelyImageAttachmentUrl(href)) {
                event?.preventDefault?.();
                event?.stopPropagation?.();
                setDescriptionPreviewImage(href);
            }
        },
        [resolveAttachmentHrefFromItems],
    );

    const currentUser = useAuthStore((s) => s.user);
    const currentUserId = currentUser?.id ?? currentUser?._id ?? currentUser?.userId ?? null;
    const can = useCanCheck();
    const canUpdateAny = can(PROJECT_BLOCKERS_UPDATE);
    const canDeleteAny = can(PROJECT_BLOCKERS_UPDATE);
    const canViewTaskDescription = can(PROJECT_TASKS_VIEW_ABILITY) || can(PROJECT_TASKS_UPDATE_ABILITY);

    const qaSubmission = useMemo(() => {
        const sub = task?.qaSubmission ?? task?.qa_submission ?? null;
        if (!sub || typeof sub !== 'object') return null;
        const notes = toMarkdownString(sub.notes);
        const content = typeof sub.content === 'string' ? sub.content.trim() : '';
        const submittedAt = sub.submittedAt ? formatActivityLogDateTime(sub.submittedAt) : '';
        const attachments = normalizeSubmissionAttachmentRecords(sub);
        const links = attachments.filter((x) => x.type === 'link');
        const files = attachments.filter((x) => x.type !== 'link');
        const linksPresentation = links.map((l) => {
            const href = normalizePhaseLinkUrl(l?.url);
            const title = String(l?.title || href || '').trim() || href || 'Link';
            return { ...l, href, title };
        });
        const filesPresentation = files.map((f) => {
            const href = resolveAttachmentUrl(f?.url);
            const isImage = isLikelyImageAttachmentUrl(f?.url);
            const ext = getAttachmentFileExtension(f?.url);
            return { ...f, href, isImage, ext, title: String(f?.title || '').trim() || String(f?.url || '').trim() || 'File' };
        });
        return {
            raw: sub,
            notes,
            content,
            submittedAt,
            submittedBy: sub.submittedBy ?? '',
            submissionId: sub.submissionId ?? '',
            links,
            files,
            linksPresentation,
            filesPresentation,
            attachmentCount: attachments.length,
        };
    }, [task]);

    const pmoSubmission = useMemo(() => {
        const sub = task?.pmoSubmission ?? task?.pmo_submission ?? null;
        if (!sub || typeof sub !== 'object') return null;
        const notes = toMarkdownString(sub.notes);
        const content = typeof sub.content === 'string' ? sub.content.trim() : '';
        const submittedAt = sub.submittedAt ? formatActivityLogDateTime(sub.submittedAt) : '';
        const attachments = normalizeSubmissionAttachmentRecords(sub);
        const links = attachments.filter((x) => x.type === 'link');
        const files = attachments.filter((x) => x.type !== 'link');
        const linksPresentation = links.map((l) => {
            const href = normalizePhaseLinkUrl(l?.url);
            const title = String(l?.title || href || '').trim() || href || 'Link';
            return { ...l, href, title };
        });
        const filesPresentation = files.map((f) => {
            const href = resolveAttachmentUrl(f?.url);
            const isImage = isLikelyImageAttachmentUrl(f?.url);
            const ext = getAttachmentFileExtension(f?.url);
            return { ...f, href, isImage, ext, title: String(f?.title || '').trim() || String(f?.url || '').trim() || 'File' };
        });
        return {
            raw: sub,
            notes,
            content,
            submittedAt,
            submittedBy: sub.submittedBy ?? '',
            submissionId: sub.submissionId ?? '',
            links,
            files,
            linksPresentation,
            filesPresentation,
            attachmentCount: attachments.length,
        };
    }, [task]);

    const consultationData = useMemo(() => {
        const c = task?.consultation ?? null;
        if (!c || typeof c !== 'object') return null;
        const description = toMarkdownString(c.description);
        const attachments = Array.isArray(c.attachments) ? c.attachments : [];
        return {
            raw: c,
            id: c.id,
            qaPassed: c.qaPassed === true,
            description,
            attachments,
            attachmentCount: attachments.length,
            consultantIds: Array.isArray(c.consultantIds) ? c.consultantIds : [],
        };
    }, [task]);

    useEffect(() => {
        if (!open) return;
        if (detailTab === 'qa' && !qaSubmission) setDetailTab('attachments');
        if (detailTab === 'consultation' && !consultationData) setDetailTab('attachments');
    }, [open, detailTab, qaSubmission, consultationData]);

    const getRoleLabel = useCallback((member) => {
        if (!member || typeof member !== 'object') return '—';
        const value =
            (typeof member.role === 'string' || typeof member.role === 'number' ? member.role : undefined) ??
            member.role?.name ??
            member.roleName ??
            member.role?.roleName ??
            member.designation ??
            member.position ??
            member.jobTitle;
        const role = String(value || '').trim();
        if (!role) return '—';
        if (role.toLowerCase() === 'pm') return 'PM';
        return role
            .replace(/[_-]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/\b\w/g, (m) => m.toUpperCase());
    }, []);

    const canManageBlocker = useCallback(
        (blocker) => {
            if (!blocker) return false;
            const createdByObj = blocker?.createdBy ?? blocker?.created_by ?? blocker?.authorId ?? blocker?.userId;
            const cId = typeof createdByObj === 'object' && createdByObj !== null ? (createdByObj.id || createdByObj._id) : createdByObj;
            return Boolean(currentUserId && String(cId ?? '') === String(currentUserId));
        },
        [currentUserId],
    );

    const handleDeleteBlocker = useCallback(
        (blocker) => {
            const blockerId = blocker?.id;
            const taskIdForApi = task?.taskId ?? task?.task_id ?? task?.id;
            if (!projectId || !taskIdForApi || !blockerId) return;
            Modal.confirm({
                title: 'Delete this blocker?',
                content: 'This cannot be undone.',
                okText: 'Yes, delete',
                cancelText: 'No',
                okType: 'danger',
                onOk: async () => {
                    try {
                        await deleteProjectTaskBlocker(projectId, taskIdForApi, blockerId);
                        message.success('Blocker deleted');
                        setHiddenBlockerIds((prev) => {
                            const next = new Set(prev);
                            next.add(String(blockerId));
                            return next;
                        });
                        onRequestRefresh?.();
                    } catch (err) {
                        message.error(err?.response?.data?.message || err?.message || 'Failed to delete blocker');
                    }
                },
            });
        },
        [projectId, task, onRequestRefresh],
    );

    const taskId = task?.taskId ?? task?.task_id ?? task?.id;
    const taskDisplayId = task?.taskNumber || task?.task_number || task?.number || getTaskDisplayId(task);
    const taskTitle = task?.title ?? task?.name ?? 'Task';
    const isDevBacklog = isTaskOnDevBacklog(task);
    const createdAtLabel = useMemo(() => {
        const raw = task?.createdAt ?? task?.created_at ?? task?.createdOn ?? task?.created_on ?? null;
        return raw ? formatLongDateTimeWithPipe(raw) : '';
    }, [task]);
    const createdByPresentation = useMemo(() => {
        const u = task?.createdBy ?? task?.created_by ?? task?.author ?? task?.owner ?? null;
        if (!u || typeof u !== 'object') return null;
        const id = u?.id ?? u?._id ?? null;
        const name = u?.name ?? u?.fullName ?? u?.email ?? null;
        if (!name) return null;
        const roleLabel = u?.role?.name ?? u?.roleName ?? u?.role ?? null;
        const url = u?.avatarUrl ?? u?.avatar_url ?? null;
        return {
            id,
            name,
            roleLabel,
            imageUrl: url ? resolveAttachmentUrl(url) : null,
            initials: getInitials(name),
            color: getAvatarColor(id || name),
        };
    }, [task]);
    const assigneePresentation = useMemo(() => {
        const u = task?.assigneeId ?? task?.assignee ?? null;
        if (!u || typeof u !== 'object') return null;
        const id = u?.id ?? null;
        const name = u?.name ?? u?.fullName ?? u?.email ?? null;
        if (!name) return null;
        const roleLabel = u?.roleLabel ?? u?.role?.name ?? u?.role ?? null;
        const url = u?.avatarUrl ?? u?.avatar_url ?? null;
        return {
            id,
            name,
            roleLabel,
            imageUrl: url ? resolveAttachmentUrl(url) : null,
            initials: getInitials(name),
            color: getAvatarColor(id || name),
        };
    }, [task]);
    const assignedAtLabel = useMemo(() => {
        const raw = task?.assignedAt ?? task?.assigned_at ?? null;
        return raw ? formatLongDateTimeWithPipe(raw) : '';
    }, [task]);
    const submittedAtLabel = task?.submittedAt ? formatActivityLogDateTime(task.submittedAt) : '';
    const submittedNotesMarkdown = toMarkdownString(task?.submittedNotes ?? task?.submitted_notes);
    const buildLink = typeof task?.buildLink === 'string' ? task.buildLink.trim() : (typeof task?.build_link === 'string' ? task.build_link.trim() : '');

    const bugsPresentation = useMemo(
        () =>
            bugs.map((bug) => ({
                bug,
                md: getBugDescriptionMarkdown(bug) || (typeof bug.description === 'string' ? bug.description.trim() : ''),
                bugHeading: bug?.bugNumber || bug?.bug_number || getBugHeading(bug),
                attachments: normalizeBugAttachmentItems(bug),
            })),
        [bugs],
    );

    const blockersPresentation = useMemo(
        () =>
            taskBlockers.map((b, idx) => {
                const createdByObj = b?.createdBy ?? b?.created_by ?? b?.author ?? b?.user ?? b?.userId;
                const createdById =
                    typeof createdByObj === 'object' && createdByObj !== null ? (createdByObj.id || createdByObj._id || '') : createdByObj;
                const createdByName =
                    typeof createdByObj === 'object' && createdByObj !== null
                        ? createdByObj.name || createdByObj.fullName || createdByObj.email || ''
                        : '';
                const createdByRoleLabel = typeof createdByObj === 'object' && createdByObj !== null ? getRoleLabel(createdByObj) : '—';
                const createdAtRaw = b?.createdAt ?? b?.created_at ?? b?.created_at_time ?? b?.createdOn ?? '';
                const createdAtLabel = createdAtRaw ? formatActivityLogDateTime(createdAtRaw) : '';
                const blockerNumber = b?.blockerNumber || b?.blocker_number || b?.number || `Blocker ${idx + 1}`;
                const desc =
                    typeof b?.description?.markdown === 'string'
                        ? b.description.markdown
                        : typeof b?.description === 'string'
                            ? b.description
                            : typeof b?.notes === 'string'
                                ? b.notes
                                : '';
                const descriptionSource = String(desc || '').trim();
                const attachmentsRaw = b?.attachments ?? b?.files ?? b?.links ?? [];
                const attachmentsList = Array.isArray(attachmentsRaw) ? attachmentsRaw : [];
                const attachmentsCount = attachmentsList.length;
                const attachmentItems = attachmentsList
                    .map((a) => {
                        if (!a) return null;
                        if (typeof a === 'string') return { type: isLikelyFileAttachmentUrl(a) ? 'file' : 'link', url: a, title: a };
                        const url = String(a.url ?? a.href ?? '').trim();
                        if (!url) return null;
                        const type = String(a.type || '').toLowerCase() === 'link' ? 'link' : 'file';
                        const title = String(a.title ?? a.name ?? a.filename ?? url);
                        return { type, url, title };
                    })
                    .filter(Boolean);

                return {
                    blocker: b,
                    blockerNumber,
                    descriptionSource,
                    attachmentItems,
                    attachmentsCount,
                    createdAtLabel,
                    createdByName,
                    createdByRoleLabel,
                    createdByInitials: getInitials(createdByName || 'User'),
                    createdByColor: getAvatarColor(createdById || createdByName || 'User'),
                    canEdit: canManageBlocker(b),
                    canDelete: canManageBlocker(b),
                };
            }).filter((row) => {
                const id = row?.blocker?.id ?? row?.blocker?._id ?? null;
                if (id == null) return true;
                return !hiddenBlockerIds.has(String(id));
            }),
        [taskBlockers, canManageBlocker, getRoleLabel, hiddenBlockerIds],
    );
    const blockerCount = blockersPresentation.length;

    const getBlockerCardStyle = useCallback(
        (blocker) => {
            const id = blocker?.id ?? blocker?._id ?? null;
            const hovered = id != null && String(id) === String(hoveredBlockerId ?? '');
            return {
                boxShadow: 'none',
                cursor: 'pointer',
                transition: 'background 160ms ease, border-color 160ms ease',
                background: hovered ? 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))' : 'var(--ant-color-bg-elevated, #fff)',
                borderColor: hovered ? 'var(--ant-color-border, #d9d9d9)' : 'var(--ant-color-border-secondary, #f0f0f0)',
            };
        },
        [hoveredBlockerId],
    );

    const activityPresentation = useMemo(() => {
        const sorted = activityLogs
            .slice()
            .sort((a, b) => {
                const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
                const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
                return tb - ta;
            });
        return sorted.map((log) => ({
            log,
            msg: (log?.message ?? '').trim() || '—',
            when: formatActivityLogDateTime(log?.createdAt),
        }));
    }, [activityLogs]);

    const attachmentFilesPresentation = useMemo(
        () =>
            attachmentFiles.map((item) => {
                const href = resolveAttachmentUrl(item.rawUrl);
                const isImage = isLikelyImageAttachmentUrl(item.rawUrl);
                const ext = getAttachmentFileExtension(item.rawUrl);
                return { item, href, isImage, ext };
            }),
        [attachmentFiles],
    );

    return {
        detailTab,
        setDetailTab,
        discussionBug,
        setDiscussionBug,
        discussionBlocker,
        setDiscussionBlocker,
        descriptionPreviewImage,
        setDescriptionPreviewImage,
        createBlockerOpen,
        setCreateBlockerOpen,
        editingBlocker,
        setEditingBlocker,
        bugFileInputRef,

        taskTypeLabel,
        hasDescription,
        resolvedTaskDescriptionMarkdown,
        attachmentItems,
        attachmentCount,
        attachmentLinks,
        attachmentFilesPresentation,
        descriptionAttachmentItems,

        bugs,
        bugCount,
        bugsPresentation,

        taskBlockers,
        blockerCount,
        blockersPresentation,
        handleDeleteBlocker,
        hoveredBlockerId,
        setHoveredBlockerId,
        getBlockerCardStyle,

        showActivityTab,
        activityCount,
        activityPresentation,

        taskId,
        taskDisplayId,
        taskTitle,
        isDevBacklog,
        createdAtLabel,
        createdByPresentation,
        assignedAtLabel,
        assigneePresentation,
        submittedAtLabel,
        submittedNotesMarkdown,
        buildLink,
        handleMarkdownImageLinkClick,
        canManageBlocker,
        canUpdateAny,
        canDeleteAny,
        canViewTaskDescription,
        qaSubmission,
        pmoSubmission,
        consultationData,

        truncateFileName,
        getAvatarColor,
        getInitials,
        normalizePhaseLinkUrl,
        isLikelyImageAttachmentUrl,
    };
}

